import React, { useState, useEffect } from 'react';
import Split from 'react-split';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { MapCard, CardDb, isMapCard, updateCard } from '../redux/cardDbSlice';
import { CardAttributeDbHashType, addCardAttribute, updateCardAttribute } from '../redux/cardAttributeDbSlice';
import { CardShapeDb, addCardShape, updateCardShape, deleteCardShape } from '../redux/cardShapeDbSlice';
import { MemoCardContentDb, updateMemoCardContent } from '../redux/memoCardContentDbSlice';
import { UiCardItemDb, addUiCardItem, updateUiCardItem, deleteUiCardItem } from '../redux/uiCardItemDbSlice';
import { DrawCardContentDb, updateDrawCardContent } from '../redux/drawCardContentDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';
import useOneCardDb from '../hooks/useOneCardDb'
import useCardAttributeDbs from '../hooks/useCardAttributeDbs';
import useCardShapeDbs from '../hooks/useCardShapeDbs';
import useMemoCardContentDb from '../hooks/useMemoCardContentDb';
import useUiCardItemDbs from '../hooks/useUiCardItemDbs';
import useDrawCardContentDb from '../hooks/useDrawCardContentDb';
import useCurrentUserDb from '../hooks/useCurrentUserDb';
import useUserCondition from '../hooks/useUserCondition';
import useWindowSize from '@rehooks/window-size';
import useElementDimensions from '../hooks/useElementDimensions';
import { deepCopy } from '../general';
import { initial } from '../card_components/index'
import EditCommonCard from '../card_components/EditCommonCard';
import EditMemoCard from '../card_components/EditMemoCard';
import EditUiCard from '../card_components/EditUiCard';
import EditDrawCard from '../card_components/EditDrawCard';
import { DrawSettingType, ActionsHashType, UndoneActionType, DrawTextHashType } from './DrawToolContent';
import Note, { OpenNoteButton } from './Note';

export type EditCardShapeDbHashType = { [key in string]: CardShapeDb | null };
export type EditUiCardItemDbHashType = { [key in string]: UiCardItemDb | null };

const useStyles = makeStyles(() => ({
  split: {
    display: 'flex',
    height: '100%',
    cursor: 'ew-resize',
    zIndex: 2,
    position: 'relative',
  },
  disableSplit: {
    display: 'flex',
    height: '100%',
    cursor: 'auto',
    zIndex: 2,
    position: 'relative',
  },
  workPanel: {
    flex: 'auto',
    cursor: 'auto',
    zIndex: 0,
    position: 'relative',
  },
  notePanel: {
    flex: 'auto',
    overflow: 'hidden',
    cursor: 'auto',
    zIndex: 1,
    position: 'relative',
    height: 'inherit',
  },
}));

const getSplitRatios = (condition?: UserCondition) => {
  if (!condition) return [70, 30];
  const ratios = condition.workNoteRatios.card;
  const workRatio = Number.isFinite(ratios[0]) ? ratios[0] : 70;
  const noteRatio = Number.isFinite(ratios[1]) ? ratios[1] : 30;
  if (condition.noteDisplay.card) return [workRatio, noteRatio];
  else return [100];
}

type EditDialogProps = {
  open: boolean;
  onClose: () => void;
  id: string;
  toggleIsGroupAndSave: (id: string, card: MapCard, isGroupOld?: boolean) => void;
}
const EditDialog: React.FC<EditDialogProps> = ({ open, onClose, id, toggleIsGroupAndSave }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const editDialogContentId = `edit-dialog-content`;
  const [card, setCard] = useState<CardDb | undefined>(undefined);
  const [attributes, setAttributes] = useState<CardAttributeDbHashType>({});
  const [shapes, setShapes] = useState<EditCardShapeDbHashType>({});
  const [memoContent, setMemoContent] = useState<MemoCardContentDb | undefined>(undefined);
  const [uiItems, setUiItems] = useState<EditUiCardItemDbHashType>({});
  const [originUiItemLength, setOriginUiItemLength] = useState<number>(0);
  const [drawContent, setDrawContent] = useState<DrawCardContentDb | undefined>(undefined);
  const cardValue = useOneCardDb(id);
  const [isGroupOld, setIsGroupOld] = useState<boolean>();
  const [attributeIds, attributeValues] = useCardAttributeDbs(id);
  const [shapeIds, shapeValues] = useCardShapeDbs(id);
  const [memoContentId, memoContentValue] = useMemoCardContentDb(id);
  const [uiItemIds, uiItemValues] = useUiCardItemDbs(id);
  const [drawContentId, drawContentValue] = useDrawCardContentDb(id);
  const [userId] = useCurrentUserDb();
  const [userConditionId, userCondition] = useUserCondition(userId);
  const [elementDrawn, setElementDrawn] = useState(false);
  const widowSize = useWindowSize();
  const { width: workWidth } = useElementDimensions(editDialogContentId, elementDrawn);
  const [setting, setSetting] = useState<DrawSettingType>(initial.drawSetting);
  const [actionIds, setActionIds] = useState<string[]>([]);
  const [actions, setActions] = useState<ActionsHashType>({});
  const [undoneActions, setUndoneActions] = useState<UndoneActionType[]>([]);
  const [textHash, setTextHash] = useState<DrawTextHashType>({});
  const [splitRatios, setSplitRatios] = useState<number[]>(getSplitRatios(userCondition));
  const [splitSizes, setSplitSizes] = useState<number[]>();
  const [noteDisplay, setNoteDisplay] = useState(false);
  const [noteLeft, setNoteLeft] = useState(0);
  const [noteTransition, setNoteTransition] = useState<string>();
  const noteTransitionTime = 180;
  const [splitUpdated, setSplitUpdated] = useState(true);

  useEffect(() => {
    setCard(cardValue);
    if (cardValue) setIsGroupOld(cardValue.isGroup);
    if (cardValue?.kind === 'common') {
      const attributeHash: CardAttributeDbHashType = {};
      attributeIds.forEach((id, ix) => attributeHash[id] = attributeValues[ix]);
      const shapeHash: EditCardShapeDbHashType = {};
      shapeIds.forEach((id, ix) => shapeHash[id] = shapeValues[ix]);
      setAttributes(attributeHash);
      setShapes(shapeHash);
    }
    else if (cardValue?.kind === 'memo') setMemoContent(memoContentValue);
    else if (cardValue?.kind === 'ui') {
      const uiItemHash: EditUiCardItemDbHashType = {};
      uiItemIds.forEach((id, ix) => uiItemHash[id] = uiItemValues[ix]);
      setUiItems(uiItemHash);
      setOriginUiItemLength(uiItemIds.length);
    }
    else if (cardValue?.kind === 'draw') {
      setDrawContent(drawContentValue);
      setSetting({...setting, 
        backgroundColor: cardValue.background.display ? cardValue.background.color : '#ffffff',
        frameDisplay: cardValue.frame.display,
        frameColor: cardValue.frame.color});
    }
  }, [cardValue])

  const initState = () => {
    setSetting(initial.drawSetting);
    setActionIds([]);
    setActions({});
    setUndoneActions([]);
    setTextHash({});
  }
  const saveDrawData = () => {
    if (drawContentId === undefined) return;
    const svg = document.getElementById('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    let hex = setting.backgroundColor;
    if (hex.slice(0, 1) === "#") hex = hex.slice(1);
    if (hex.length === 3) hex = hex.slice(0, 1) + hex.slice(0, 1) + hex.slice(1, 2) + hex.slice(1, 2) + hex.slice(2, 3) + hex.slice(2, 3);
    const rgb = [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)].map((str) => {
      return parseInt(str, 16);
    });

    const canvas = document.createElement('canvas');
    canvas.setAttribute('width', String(initial.cardWidth * 4));
    canvas.setAttribute('height', String(initial.cardHeight * 4));
    let context = canvas.getContext('2d');
    let image = new Image();
    image.onload = function(){
      if (!context) return;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      let imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      for(let i = 0; i < (imageData.width * imageData.height); i++) {
        if((imageData.data[i * 4] === rgb[0]) &&
          (imageData.data[i * 4 + 1] === rgb[1]) &&
          (imageData.data[i * 4 + 2] === rgb[2])) {
          imageData.data[i * 4 + 3] = 0;
        }
      }
      context.putImageData(imageData, 0, 0);
      const dataUrlBase64 = canvas.toDataURL('image/png');
      dispatch(updateDrawCardContent({id: drawContentId, content: dataUrlBase64}));
    }

    image.src = 'data:image/svg+xml;charset=utf-8;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }
  const saveCard = () => {
    if (id && card) {
      if (isMapCard(card)) toggleIsGroupAndSave(id, card, isGroupOld);
      else dispatch(updateCard({ id, ...card }));

      if (card.kind === 'common') {
        const attributeIds = Object.keys(attributes);
        attributeIds.forEach((id, ix) => {
          const attribute = attributes[id];
          const prefix = id.slice(0, 12);
          // add new attribute
          if (prefix === 'newAttribute') dispatch(addCardAttribute(attribute));
          // update existing attribute
          else dispatch(updateCardAttribute({id: id, ...attribute}));
        })
        const shapeIds = Object.keys(shapes);
        shapeIds.forEach(id => {
          const shape = shapes[id];
          const prefix = id.slice(0, 8);
          // add new shape
          if (prefix === 'newShape' && shape) dispatch(addCardShape(shape));
          // update existing shape
          else if (shape) dispatch(updateCardShape({id: id, ...shape}));
          // delete existing shape
          else if (prefix !== 'newShape' && shape === null) dispatch(deleteCardShape({id: id}));
        })
      }
      if (card.kind === 'memo' && memoContentId) dispatch(updateMemoCardContent({id: memoContentId, ...memoContent}));
      if (card.kind === 'ui') {
        const ids = Object.keys(uiItems);
        ids.forEach((id, ix) => {
          const item = uiItems[id];
          // add new item
          if (ix + 1 > originUiItemLength && item) dispatch(addUiCardItem({id: id, ...item}));
          // update existing item
          else if (item) dispatch(updateUiCardItem({id: id, ...item}));
          // delete existing item
          else if (ix + 1 <= originUiItemLength && item === null) dispatch(deleteUiCardItem({id: id}));
        })
      }
      if (card.kind === 'draw') saveDrawData();
    }
    onClose();
  }
  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition) return;
    setSplitUpdated(false);
    const newRatio = deepCopy(userCondition.workNoteRatios);
    const newSizes = sizes;
    newRatio.card = newSizes;
    dispatch(updateUserCondition({ id: userConditionId, workNoteRatios: newRatio }));
    setSplitRatios(newRatio.card);
    setTimeout(() => setSplitUpdated(true), 1);
  }
  const toggleNote = (value: boolean) => {
    if (!userConditionId || !userCondition || value === noteDisplay) return;
    setSplitUpdated(false);
    const newNoteDisplay = deepCopy(userCondition.noteDisplay);
    newNoteDisplay.card = value;

    if (workWidth) setNoteLeft(workWidth * userCondition.workNoteRatios.card[1] / 100);

    setNoteTransition(`${noteTransitionTime}ms ease-out`);
    if (value) setNoteDisplay(value);

    if (value) {
      dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
      setTimeout(() => setNoteLeft(0), 1);
    } else {
      setTimeout(() => {
        dispatch(updateUserCondition({ id: userConditionId, noteDisplay: newNoteDisplay }));
      }, noteTransitionTime);
    }
    setTimeout(() => {
      setNoteTransition(undefined);
      setSplitUpdated(true);
    }, noteTransitionTime);
  }

  useEffect(() => {
    if (!userCondition || !card) return;
    setSplitRatios(getSplitRatios(userCondition));
    setNoteDisplay(userCondition.noteDisplay.card && card?.kind === 'common');
  }, [userCondition, id, card])

  useEffect(() => {
    setSplitRatios(getSplitRatios(userCondition));
  }, [noteDisplay])

  useEffect(() => {
    setElementDrawn(false);
    setTimeout(() => setElementDrawn(true), 1);
  }, [useElementDimensions(editDialogContentId), id])

  useEffect(() => {
    if (splitSizes && noteDisplay) dragSplit(splitSizes);
  }, [splitSizes])

  return (
    <Dialog id="editDialog" open={open} onClose={onClose} onExited={initState} fullWidth={true} maxWidth={card && (card.cardTemplateId === 'MemoCard') ? 'sm' : 'xl'}>
      <DialogTitle>カードを編集します</DialogTitle>
      <DialogContent id={editDialogContentId} style={{ height: card?.kind === 'common' ? widowSize.innerHeight - 64 - 116.5 : undefined, overflow: 'hidden' }}>
        <Split
          expandToMin={false}
          gutterSize={5}
          gutterAlign="center"
          snapOffset={30}
          dragInterval={1}
          direction="horizontal"
          sizes={splitRatios}
          onDrag={sizes => setSplitSizes(sizes)}
          className={noteDisplay ? classes.split : classes.disableSplit}
        >
          <div className={classes.workPanel}>
            { card?.kind === 'common' &&
              <EditCommonCard cardId={id} card={card} setCard={setCard}
                attributes={attributes} setAttributes={setAttributes}
                shapes={shapes} setShapes={setShapes}
                splitUpdated={splitUpdated}
              />
            }
            { (card?.kind === 'memo' && memoContent) && <EditMemoCard card={card} setCard={setCard} memoContent={memoContent} setMemoContent={setMemoContent} /> }
            { card?.kind === 'ui' && <EditUiCard cardId={id} card={card} setCard={setCard} uiItems={uiItems} setUiItems={setUiItems} /> }
            { (card?.kind === 'draw' && drawContent) &&
              <EditDrawCard card={card} setCard={setCard}
                drawToolContentProps={{
                  dataUrl: drawContent.content, setting, setSetting, actionIds, setActionIds,
                  actions, setActions, undoneActions, setUndoneActions, textHash, setTextHash
                }}
              />
            }
            { card?.kind === 'common' &&
              <OpenNoteButton noteDisplay={noteDisplay} openNote={() => toggleNote(true)} />
            }
          </div>
          <div className={classes.notePanel} style={{ display: noteDisplay ? 'block' : 'none', left: noteLeft, transition: noteTransition }}>
            <Note
              reference="card" referenceId={id}
              toggleNote={() => toggleNote(false)}
            />
          </div>
        </Split>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" color="primary">
          キャンセル
        </Button>
        <Button variant="text" color="primary" onClick={saveCard}>
          保存する
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditDialog;
